import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { VowHeader } from "@/components/VowHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getISTMinutes = () => {
  const now = new Date();
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(now);
  const hh = +p.find(x => x.type === 'hour').value;
  const mm = +p.find(x => x.type === 'minute').value;
  return hh * 60 + mm;
};

const toMin = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

/** Check if current IST falls within any slot (handles midnight wrap) */
const isTimeInSlots = (slots) => {
  if (!slots || slots.length === 0) return true;
  const cur = getISTMinutes();
  return slots.some(s => {
    const from = toMin(s.available_from || s.start);
    const to = toMin(s.available_until || s.end);
    if (from <= to) return cur >= from && cur <= to;
    // Midnight wrap (e.g. 22:00–08:00)
    return cur >= from || cur <= to;
  });
};

/** Build full availability text showing ALL windows for a section */
const allSlotsText = (slots) => {
  if (!slots || slots.length === 0) return null;
  const windows = slots.map(s => {
    const from = s.available_from || s.start || "";
    const to = s.available_until || s.end || "";
    return from && to ? `${from}–${to}` : null;
  }).filter(Boolean);
  if (windows.length === 0) return null;
  return windows.join(" & ");
};

const normalizeDiet = (raw) => {
  if (!raw) return "veg";
  const t = String(raw).toLowerCase().trim();
  if (t === "non-veg" || t === "nonveg") return "non-veg";
  if (t === "egg") return "egg";
  if (t === "beverage" || t === "drink") return "beverage";
  if (t === "mrp") return "mrp";
  return "veg";
};

const DIET_COLORS = { veg: "#22a722", "non-veg": "#e53e3e", egg: "#d4a017" };

const DietDot = ({ type }) => {
  const t = normalizeDiet(type);
  if (t === "beverage" || t === "mrp") return null;
  const c = DIET_COLORS[t] || DIET_COLORS.veg;
  const label = t === "non-veg" ? "NON-VEG" : t.toUpperCase();
  return (
    <span className="inline-flex items-center gap-1" data-testid={`diet-badge-${t}`}>
      <span className="w-4 h-4 rounded-sm border-2 flex items-center justify-center" style={{ borderColor: c }}>
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }}></span>
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: c }}>{label}</span>
    </span>
  );
};

const FoodImage = ({ item }) => {
  const [failed, setFailed] = useState(false);
  const imgUrl = item.image_url || "";
  if (!imgUrl || failed) return null;
  return (
    <img src={imgUrl} alt={item.name}
      className="w-[60px] h-[60px] rounded-lg object-cover flex-shrink-0"
      onError={() => setFailed(true)}
      data-testid={`food-image-${item.name}`} />
  );
};

const MenuBrowsing = () => {
  const { roomNumber, brandName } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [dietFilter, setDietFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cartKey = `cart_${roomNumber}_${brandName}`;
    const saved = localStorage.getItem(cartKey);
    if (saved) { try { setCart(JSON.parse(saved)); } catch { localStorage.removeItem(cartKey); } }
    loadMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomNumber, brandName]);

  useEffect(() => {
    localStorage.setItem(`cart_${roomNumber}_${brandName}`, JSON.stringify(cart));
  }, [cart, roomNumber, brandName]);

  const loadMenu = async () => {
    try {
      const [catRes, menuRes] = await Promise.all([
        axios.get(`${API}/categories/${encodeURIComponent(brandName)}`),
        axios.get(`${API}/menu/${encodeURIComponent(brandName)}`),
      ]);
      setCategories(catRes.data);
      // Filter out admin-disabled items
      setMenuItems(menuRes.data.filter(i => i.available !== false));
    } catch { toast.error("Failed to load menu"); }
    finally { setLoading(false); }
  };

  const addToCart = (item) => {
    const cartKey = `cart_${roomNumber}_${brandName}`;
    const existing = cart.find(i => i.name === item.name && i.category === item.category);
    const updated = existing
      ? cart.map(i => i.name === item.name && i.category === item.category ? { ...i, quantity: i.quantity + 1 } : i)
      : [...cart, { ...item, quantity: 1 }];
    setCart(updated);
    localStorage.setItem(cartKey, JSON.stringify(updated));
    toast.success(`${item.name} added`);
  };

  // Filter by search + diet. Beverages + MRP always visible.
  const filtered = menuItems.filter(item => {
    const matchSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    const dt = normalizeDiet(item.diet_type);
    const matchDiet = dietFilter === "all" || dt === dietFilter || dt === "beverage" || dt === "mrp";
    return matchSearch && matchDiet;
  });

  // Group by category, only items whose time_slots match NOW
  const grouped = {};
  for (const cat of categories) {
    const catItems = filtered.filter(i => i.category === cat.name);
    // Per-item time check
    const available = catItems.filter(i => isTimeInSlots(i.time_slots));
    grouped[cat.name] = available;
  }

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const dietOpts = [
    { key: "all", label: "All" },
    { key: "veg", label: "Veg" },
    { key: "non-veg", label: "Non-Veg" },
    { key: "egg", label: "Egg" },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#1c3b2e] border-t-transparent"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-24">
      <VowHeader roomNumber={roomNumber} onBack={() => navigate(`/room/${roomNumber}`)}>
        <button onClick={() => navigate(`/cart/${roomNumber}/${encodeURIComponent(brandName)}`)}
          className="relative p-2 rounded-full hover:bg-white/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          data-testid="cart-button">
          <ShoppingCart className="h-5 w-5" />
          {cartCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-[#bd9b5e] text-[#1c3b2e] text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center cart-badge"
              data-testid="cart-count">{cartCount}</span>
          )}
        </button>
      </VowHeader>

      {/* Search + Filter */}
      <div className="px-4 pt-4 pb-2 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#2c2c2c]/40" />
          <Input type="text" placeholder="Search menu..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 h-11 rounded-xl bg-white border-[#1c3b2e]/10 text-sm focus-visible:ring-[#1c3b2e]"
            data-testid="search-input" />
        </div>
        <div className="flex gap-2" data-testid="diet-filter-group">
          {dietOpts.map(o => (
            <button key={o.key} onClick={() => setDietFilter(o.key)}
              className={`px-3.5 py-2 rounded-full text-xs font-semibold transition-colors min-h-[36px] ${
                dietFilter === o.key
                  ? "bg-[#1c3b2e] text-white"
                  : "bg-white text-[#2c2c2c]/60 border border-[#1c3b2e]/10 hover:border-[#1c3b2e]/30"
              }`}
              data-testid={`diet-filter-${o.key}`}>{o.label}</button>
          ))}
        </div>
      </div>

      {/* Menu Sections */}
      <div className="px-4 pt-2 pb-4 space-y-5">
        {categories.map(cat => {
          const items = grouped[cat.name] || [];
          const allCatItems = filtered.filter(i => i.category === cat.name);
          const isActive = items.length > 0;
          const hasItemsButInactive = allCatItems.length > 0 && !isActive;

          // Section has items but none available now — show greyed header
          if (hasItemsButInactive) {
            const slotText = allSlotsText(cat.available_slots);
            return (
              <div key={cat.name} data-testid={`category-${cat.name}`}
                className="flex items-center justify-between py-3 px-4 rounded-xl bg-white/60 border border-dashed border-[#1c3b2e]/10">
                <span className="text-sm font-semibold text-[#2c2c2c]/40">{cat.name}</span>
                {slotText && (
                  <span className="flex items-center gap-1 text-[11px] text-[#2c2c2c]/40">
                    <Clock className="h-3 w-3" />Available {slotText}
                  </span>
                )}
              </div>
            );
          }

          // No items at all for this section (filtered out)
          if (!isActive) return null;

          const isMRP = cat.name === "Packaged Items (MRP)";

          return (
            <div key={cat.name} data-testid={`category-${cat.name}`}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-base font-bold text-[#1c3b2e]">{cat.name}</h2>
                <div className="flex-1 h-px bg-[#1c3b2e]/10"></div>
              </div>

              {isMRP ? (
                /* MRP items: compact text-only rows, no images */
                <div className="bg-white rounded-xl border border-[#1c3b2e]/5 divide-y divide-[#1c3b2e]/5">
                  {items.map(item => (
                    <div key={`${item.category}-${item.name}`}
                      className="flex items-center justify-between px-4 py-3"
                      data-testid={`menu-item-${item.name}`}>
                      <div>
                        <p className="text-sm font-medium text-[#2c2c2c]">{item.name}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-[#1c3b2e]">₹{item.price}</span>
                        <button onClick={() => addToCart(item)}
                          className="bg-[#1c3b2e] text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg min-h-[36px]"
                          data-testid={`add-to-cart-${item.name}`}>ADD</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Regular items: image + diet dot + description */
                <div className="space-y-2.5">
                  {items.map(item => (
                    <div key={`${item.category}-${item.name}`}
                      className="bg-white rounded-xl p-3 border border-[#1c3b2e]/5"
                      data-testid={`menu-item-${item.name}`}>
                      <div className="flex items-center gap-3">
                        <FoodImage item={item} />
                        <div className="flex-1 min-w-0">
                          <DietDot type={item.diet_type} />
                          <h3 className="font-semibold text-[13px] text-[#2c2c2c] mt-0.5 leading-snug">{item.name}</h3>
                          {item.description && (
                            <p className="text-[11px] text-[#2c2c2c]/45 mt-0.5 line-clamp-1">{item.description}</p>
                          )}
                          <p className="text-sm font-bold text-[#1c3b2e] mt-1">₹{item.price}</p>
                        </div>
                        <button onClick={() => addToCart(item)}
                          className="flex-shrink-0 bg-[#1c3b2e] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#1c3b2e]/90 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                          data-testid={`add-to-cart-${item.name}`}>
                          ADD
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sticky Cart Bar */}
      {cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-3 bg-[#f5f1e6]/95 backdrop-blur-md border-t border-[#1c3b2e]/10">
          <div className="max-w-md mx-auto">
            <button onClick={() => navigate(`/cart/${roomNumber}/${encodeURIComponent(brandName)}`)}
              className="w-full bg-[#1c3b2e] text-white rounded-xl h-14 flex items-center justify-between px-5 hover:bg-[#1c3b2e]/90 transition-colors min-h-[56px]"
              data-testid="view-cart-button">
              <span className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                <span className="font-semibold">{cartCount} {cartCount === 1 ? "item" : "items"}</span>
              </span>
              <span className="font-bold text-[#bd9b5e]">₹{cartTotal.toFixed(0)} →</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuBrowsing;
